import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChartBar, TrendUp, CreditCard, Lightning, Sparkle, Image, VideoCamera, UserCircle, Microphone } from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const C = { bg: '#080808', card: '#141414', border: 'rgba(255,255,255,0.07)', text: '#f5f0e8', dim: 'rgba(245,240,232,0.4)', accent: '#c8f060' };

const FEATURE_LABELS = {
  flyer_generate: 'Flyer', ai_flyer: 'Custom Flyer', carousel: 'Carousel', carousel_generate: 'Carousel',
  video_ad: 'Video Ad', character_generate: 'Character', voiceover: 'Voiceover',
  competitive_intel: 'Comp Intel', landing_page: 'Landing Page', content: 'Content', copy: 'Copy',
  email: 'Email', seo: 'SEO', meta_ads: 'Meta Ads', ab_test: 'A/B Test',
};

const FEATURE_COLORS = {
  flyer_generate: '#c8f060', ai_flyer: '#a78bfa', carousel: '#22d3ee', carousel_generate: '#22d3ee',
  video_ad: '#f59e0b', character_generate: '#ec4899', voiceover: '#06b6d4',
  competitive_intel: '#f97316', landing_page: '#8b5cf6',
};

export default function AnalyticsDashboardPage() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/analytics/pro`, { headers: { Authorization: `Bearer ${token}` } });
      setOverview(data);
    } catch (err) {
      console.error('Analytics fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <DashboardLayout><div className="flex items-center justify-center py-32"><div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" /></div></DashboardLayout>;

  const o = overview;
  const maxDaily = Math.max(...(o?.daily_activity?.map(d => d.count) || [1]), 1);
  const maxFeature = Math.max(...(o?.feature_breakdown?.map(f => f.credits_used) || [1]), 1);

  return (
    <DashboardLayout>
      <div data-testid="analytics-dashboard" className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.06)', border: `1px solid ${C.border}` }}>
            <ChartBar size={20} style={{ color: C.accent }} />
          </div>
          <div>
            <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]" style={{ color: C.text }}>Analytics</h1>
            <p className="text-sm" style={{ color: C.dim }}>Your content performance at a glance</p>
          </div>
        </motion.div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total Content', value: o?.content_totals?.total || 0, icon: Sparkle, color: C.accent },
            { label: 'Credits Balance', value: o?.credits?.balance || 0, icon: Lightning, color: '#c8f060' },
            { label: 'Used (30d)', value: o?.credits?.used_30d || 0, icon: CreditCard, color: '#f59e0b' },
            { label: 'Videos Created', value: o?.content_totals?.videos || 0, icon: VideoCamera, color: '#a78bfa' },
          ].map((kpi, i) => (
            <motion.div key={kpi.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="rounded-xl p-4" style={{ background: C.card, border: `1px solid ${C.border}` }}>
              <div className="flex items-center gap-2 mb-2">
                <kpi.icon size={14} weight="fill" style={{ color: kpi.color }} />
                <span className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.dim }}>{kpi.label}</span>
              </div>
              <p className="text-2xl font-bold font-['Instrument_Serif',serif]" style={{ color: C.text }}>{kpi.value}</p>
            </motion.div>
          ))}
        </div>

        {/* ── Content Breakdown ── */}
        <div className="grid grid-cols-5 gap-2">
          {[
            { label: 'Flyers', count: o?.content_totals?.flyers || 0, icon: Image, color: '#c8f060' },
            { label: 'Carousels', count: o?.content_totals?.carousels || 0, icon: Image, color: '#22d3ee' },
            { label: 'Videos', count: o?.content_totals?.videos || 0, icon: VideoCamera, color: '#f59e0b' },
            { label: 'Characters', count: o?.content_totals?.characters || 0, icon: UserCircle, color: '#ec4899' },
            { label: 'Voiceovers', count: 0, icon: Microphone, color: '#06b6d4' },
          ].map(item => (
            <div key={item.label} className="rounded-xl p-3 text-center" style={{ background: C.card, border: `1px solid ${C.border}` }}>
              <item.icon size={18} weight="fill" className="mx-auto mb-1" style={{ color: item.color }} />
              <p className="text-lg font-bold font-['Instrument_Serif',serif]" style={{ color: C.text }}>{item.count}</p>
              <p className="text-[9px]" style={{ color: C.dim }}>{item.label}</p>
            </div>
          ))}
        </div>

        {/* ── Daily Activity Chart ── */}
        <div className="rounded-xl p-5" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendUp size={16} style={{ color: C.accent }} />
              <span className="text-sm font-medium" style={{ color: C.text }}>Daily Activity (30 days)</span>
            </div>
            <span className="text-[10px]" style={{ color: C.dim }}>{o?.credits?.used_30d || 0} credits used</span>
          </div>
          <div className="flex items-end gap-[3px] h-24">
            {(o?.daily_activity || []).map((day, i) => (
              <div key={day.date} className="flex-1 flex flex-col items-center justify-end group relative">
                <div className="w-full rounded-t transition-all" style={{
                  height: `${Math.max(2, (day.count / maxDaily) * 80)}px`,
                  background: day.count > 0 ? C.accent : 'rgba(255,255,255,0.04)',
                  opacity: day.count > 0 ? 0.7 : 1,
                }} />
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 px-2 py-0.5 rounded text-[8px] font-medium whitespace-nowrap"
                  style={{ background: '#222', color: C.text }}>
                  {day.date.slice(5)} — {day.count}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[8px]" style={{ color: C.dim }}>{o?.daily_activity?.[0]?.date?.slice(5)}</span>
            <span className="text-[8px]" style={{ color: C.dim }}>{o?.daily_activity?.[29]?.date?.slice(5)}</span>
          </div>
        </div>

        {/* ── Feature Breakdown Bar Chart ── */}
        <div className="rounded-xl p-5" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: C.text }}>Credits by Feature</h3>
          <div className="space-y-3">
            {(o?.feature_breakdown || []).map(item => {
              const label = FEATURE_LABELS[item.feature] || item.feature;
              const color = FEATURE_COLORS[item.feature] || '#c8f060';
              const pct = (item.credits_used / maxFeature) * 100;
              return (
                <div key={item.feature}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-medium" style={{ color: C.text }}>{label}</span>
                    <span className="text-[11px] font-bold" style={{ color }}>{item.credits_used} credits</span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }}
                      className="h-full rounded-full" style={{ background: color }} />
                  </div>
                </div>
              );
            })}
            {(!o?.feature_breakdown || o.feature_breakdown.length === 0) && (
              <p className="text-center py-8 text-sm" style={{ color: C.dim }}>No credit usage yet. Create some content to see analytics.</p>
            )}
          </div>
        </div>

        {/* ── Recent Transactions ── */}
        <div className="rounded-xl p-5" style={{ background: C.card, border: `1px solid ${C.border}` }}>
          <h3 className="text-sm font-medium mb-4" style={{ color: C.text }}>Recent Credit Transactions</h3>
          <div className="space-y-2">
            {(o?.recent_transactions || []).slice(0, 8).map((tx, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                <div>
                  <p className="text-[11px] font-medium" style={{ color: C.text }}>{FEATURE_LABELS[tx.feature] || tx.feature}</p>
                  <p className="text-[9px]" style={{ color: C.dim }}>{tx.created_at ? new Date(tx.created_at).toLocaleString() : ''}</p>
                </div>
                <div className="text-right">
                  <span className="text-[11px] font-bold" style={{ color: '#ef4444' }}>-{tx.cost}</span>
                  {tx.balance_after !== undefined && <p className="text-[9px]" style={{ color: C.dim }}>bal: {tx.balance_after}</p>}
                </div>
              </div>
            ))}
            {(!o?.recent_transactions || o.recent_transactions.length === 0) && (
              <p className="text-center py-6 text-sm" style={{ color: C.dim }}>No transactions yet.</p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
